import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Mail, Loader2, Trash2, Copy, Eye, ChevronLeft, Clock, Plus, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function ReportingPage() {
  const { isAdmin } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [reportLang, setReportLang] = useState('en');

  // Load user language preference
  useEffect(() => {
    api.getUserPreferences().then(r => { if (r.data.lang) setReportLang(r.data.lang); }).catch(() => {});
  }, []);
  const [activeReport, setActiveReport] = useState(null);
  const [editedBody, setEditedBody] = useState('');
  const [editing, setEditing] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listReports();
      setReports(res.data);
    } catch {
      toast.error('Failed to load reports');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.generateInvestorEmail('en');
      toast.success('Investor email drafted');
      setActiveReport(res.data);
      setEditedBody(res.data.body);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate email');
    }
    setGenerating(false);
  };

  const handleView = async (reportId) => {
    try {
      const res = await api.getReport(reportId);
      setActiveReport(res.data);
      setEditedBody(res.data.body);
    } catch {
      toast.error('Failed to load report');
    }
  };

  const handleSave = async () => {
    if (!activeReport) return;
    setSaving(true);
    try {
      await api.updateReport(activeReport.id, editedBody);
      toast.success('Draft saved');
      setActiveReport(prev => ({ ...prev, body: editedBody }));
    } catch {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const handleCopy = async () => {
    try {
      const blob = new Blob([editedBody], { type: 'text/html' });
      const plainBlob = new Blob([editedBody.replace(/<[^>]+>/g, '')], { type: 'text/plain' });
      await navigator.clipboard.write([new ClipboardItem({ 'text/html': blob, 'text/plain': plainBlob })]);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback to plain text
      await navigator.clipboard.writeText(editedBody.replace(/<[^>]+>/g, ''));
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleTranslate = async () => {
    setTranslating(true);
    try {
      const res = await api.translateToGerman(editedBody);
      setEditedBody(res.data.translated);
      toast.success('Translated to German');
    } catch (err) { toast.error(err.response?.data?.detail || 'Translation failed'); }
    setTranslating(false);
  };


  const handleDelete = async (reportId) => {
    try {
      await api.deleteReport(reportId);
      toast.success('Report deleted');
      if (activeReport?.id === reportId) {
        setActiveReport(null);
        setEditedBody('');
      }
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleBack = () => {
    setActiveReport(null);
    setEditedBody('');
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );

  // ─── Detail/Edit View ────────────────────────────────────
  if (activeReport) {
    const hasChanges = editedBody !== activeReport.body;
    return (
      <div className="space-y-4 max-w-5xl" data-testid="report-detail-view">
        <div className="flex items-center justify-between">
          <button onClick={handleBack}
            data-testid="report-back-btn"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ChevronLeft className="w-4 h-4" /> Back to Reports
          </button>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button onClick={() => setEditing(!editing)} variant="ghost"
                className="text-muted-foreground hover:text-foreground rounded-sm text-xs h-8 px-3">
                {editing ? 'Preview' : 'Edit HTML'}
              </Button>
            )}
            {hasChanges && (
              <Button onClick={handleSave} disabled={saving}
                data-testid="report-save-btn"
                className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8 px-3">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save Changes'}
              </Button>
            )}
            <Button onClick={handleCopy} variant="outline"
              data-testid="report-copy-btn"
              className="border-border text-foreground rounded-sm text-xs h-8 px-3">
              {copied
                ? <><Check className="w-3.5 h-3.5 mr-1.5 text-emerald-400" /> Copied</>
                : <><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy to Clipboard</>}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-sm bg-blue-600/10 border border-blue-600/20 flex items-center justify-center">
            <Mail className="w-4.5 h-4.5 text-blue-400" />
          </div>
          <div>
            <h2 className="text-base font-medium text-foreground">Investor Update</h2>
            <p className="text-[10px] text-muted-foreground font-mono">
              {new Date(activeReport.created_at).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
              {activeReport.created_by && ` \u00b7 ${activeReport.created_by}`}
            </p>
          </div>
        </div>

        {editing ? (
          <textarea
            value={editedBody}
            onChange={e => setEditedBody(e.target.value)}
            data-testid="report-body-editor"
            className="w-full bg-card border border-border rounded-sm p-4 text-sm text-foreground font-mono leading-relaxed resize-none focus:outline-none focus:ring-1 focus:ring-blue-600/30"
            style={{ minHeight: '60vh' }}
          />
        ) : (
          <div className="bg-white border border-border rounded-sm p-6">
            <div className="text-sm text-gray-800 leading-relaxed" data-testid="report-body-display"
              dangerouslySetInnerHTML={{ __html: editedBody }} />
          </div>
        )}
      </div>
    );
  }

  // ─── List View ──────────────────────────────────────────────
  return (
    <div className="space-y-6 max-w-5xl" data-testid="reporting-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl text-foreground tracking-tight">Reporting Center</h1>
          <p className="text-sm text-muted-foreground mt-1">Draft investor update emails with budget and pipeline data</p>
        </div>
        {isAdmin && (
        <div className="flex items-center gap-2">
          <Button onClick={handleGenerate} disabled={generating}
            data-testid="generate-email-btn"
            className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-sm h-9 px-4">
            {generating
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
              : <><Plus className="w-4 h-4 mr-2" /> Draft Investor Update</>}
          </Button>
        </div>
        )}
      </div>

      {reports.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center">
          <Mail className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-foreground font-medium">No reports yet</p>
          <p className="text-xs text-muted-foreground mt-1">Click "Draft Investor Update" to generate your first email</p>
        </div>
      ) : (
        <div className="space-y-2">
          {reports.map(r => (
            <div key={r.id}
              className="bg-card border border-border rounded-sm p-4 flex items-center gap-4 group"
              data-testid={`report-row-${r.id}`}>
              <div className="w-10 h-10 rounded-sm bg-blue-600/10 border border-blue-600/20 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">Investor Update</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="flex items-center gap-1 text-[10px] text-muted-foreground font-mono">
                    <Clock className="w-3 h-3" />
                    {new Date(r.created_at).toLocaleDateString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                  </span>
                  {r.created_by && (
                    <span className="text-[10px] text-muted-foreground">by {r.created_by}</span>
                  )}
                  {r.updated_at && (
                    <span className="text-[10px] text-blue-400/60 font-mono">edited</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="ghost" size="sm"
                  onClick={() => handleView(r.id)}
                  data-testid={`report-view-${r.id}`}
                  className="h-7 text-[11px] text-muted-foreground hover:text-foreground">
                  <Eye className="w-3.5 h-3.5 mr-1" /> View
                </Button>
                {isAdmin && (
                <button
                  onClick={() => handleDelete(r.id)}
                  data-testid={`report-delete-${r.id}`}
                  className="text-muted-foreground/30 hover:text-red-400 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
